<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-lg font-medium">
        Are you a current employee?
      </h3>
      <div class="mt-4 flex space-x-4">
        <Button
          :color="employmentStatus ? 'primary' : 'gray'"
          :variant="employmentStatus ? 'solid' : 'outline'"
          @click="employmentStatus = true"
        >
          Current Employee
        </Button>
        <Button
          :color="employmentStatus === false ? 'primary' : 'gray'"
          :variant="employmentStatus === false ? 'solid' : 'outline'"
          @click="employmentStatus = false"
        >
          Former Employee
        </Button>
      </div>
    </div>
    <div class="pt-6 border-t">
      <h3 class="text-lg font-medium mb-4">
        What is your job role?
      </h3>
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Select your job role
        </label>
        <RoleAutocomplete
          ref="roleAutocompleteRef"
          :permalink="permalink"
          :initial-role="roleTitle"
          @update:selected="handleRoleSelect"
          @update:input="handleRoleInput"
        />
        <div
          v-if="debug"
          class="mt-2 text-xs text-red-500 bg-red-50 p-2 rounded"
        >
          <p>Initial socCodeId: {{ initialSocCodeId }}</p>
          <p>Role Title: {{ roleTitle }}</p>
          <p>Selected Role: {{ JSON.stringify(selectedRole) }}</p>
        </div>
        <p class="mt-1 text-xs text-gray-500">
          Select a standardized job role from the list
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'
import RoleAutocomplete from '~/components/company/filters/RoleAutocomplete.vue'

interface Role {
  title: string
  popular_title?: string
  soc_code: string
  soc_code_id: string
  count?: number
}
interface RoleApiResponse {
  success: boolean
  role?: {
    title: string
    popular_title?: string
    soc_code: string
    soc_code_id: string
  }
}
const props = defineProps<{
  employmentStatus: boolean
  initialSocCodeId?: string
  permalink: string
  occupationTitle?: string
  occupationPopularTitle?: string
}>()
const emit = defineEmits([
  'update:employment-status',
  'update:job-title',
  'update:soc-code-id'
])
const employmentStatus = ref(props.employmentStatus)
const selectedRole = ref<Role | null>(null)
const roleAutocompleteRef = ref<any>(null)
const roleTitle = ref('')
const debug = ref(false)
const handleRoleSelect = (role: Role | null) => {
  if (!role) {
    selectedRole.value = null
    emit('update:soc-code-id', '')
    emit('update:job-title', '')
    return
  }
  selectedRole.value = role
  emit('update:soc-code-id', role.soc_code_id)
  const displayTitle = role.popular_title || role.title
  emit('update:job-title', displayTitle)
}
const handleRoleInput = (input: string) => {
  if (!input && selectedRole.value) {
    selectedRole.value = null
    emit('update:soc-code-id', '')
    emit('update:job-title', '')
  }
}
watch(employmentStatus, (newValue) => {
  emit('update:employment-status', newValue)
})
onMounted(async () => {
  if (props.initialSocCodeId) {
    try {
      if (
        (props.occupationTitle || props.occupationPopularTitle)
        && props.initialSocCodeId
      ) {
        const displayTitle
          = props.occupationPopularTitle || props.occupationTitle || ''
        roleTitle.value = displayTitle
        emit('update:job-title', displayTitle)
        emit('update:soc-code-id', props.initialSocCodeId)
        selectedRole.value = {
          title: props.occupationTitle || displayTitle,
          popular_title: props.occupationPopularTitle,
          soc_code: '',
          soc_code_id: props.initialSocCodeId
        }
        await nextTick()
        setTimeout(async () => {
          if (roleAutocompleteRef.value) {
            await roleAutocompleteRef.value.fetchRoles()
            const foundRole
              = await roleAutocompleteRef.value.findRoleBySocCodeId(
                props.initialSocCodeId
              )
            if (foundRole) {
              roleAutocompleteRef.value.searchInput = displayTitle
              roleAutocompleteRef.value.selectRole(foundRole)
            } else {
              roleAutocompleteRef.value.selectedRole = selectedRole.value
              roleAutocompleteRef.value.searchInput = displayTitle
            }
          }
        }, 500)
      } else {
        const response = await $fetch<RoleApiResponse>(
          `/api/roles/${props.initialSocCodeId}`
        )
        if (response?.success && response.role) {
          const apiRole = response.role
          const displayTitle = apiRole.popular_title || apiRole.title
          roleTitle.value = displayTitle
          emit('update:job-title', displayTitle)
          emit('update:soc-code-id', props.initialSocCodeId)
          selectedRole.value = {
            title: apiRole.title,
            popular_title: apiRole.popular_title,
            soc_code: apiRole.soc_code || '',
            soc_code_id: props.initialSocCodeId
          }
          await nextTick()
          setTimeout(async () => {
            if (roleAutocompleteRef.value) {
              await roleAutocompleteRef.value.fetchRoles()
              const foundRole
                = await roleAutocompleteRef.value.findRoleBySocCodeId(
                  props.initialSocCodeId
                )
              if (foundRole) {
                roleAutocompleteRef.value.searchInput = displayTitle
                roleAutocompleteRef.value.selectRole(foundRole)
              } else {
                roleAutocompleteRef.value.selectedRole = selectedRole.value
                roleAutocompleteRef.value.searchInput = displayTitle
              }
            }
          }, 500)
        }
      }
    } catch (error) {
      console.error('Error setting up initial role:', error)
    }
  }
})
</script>
